import * as React from "react";

import { Icon, type IconName } from "../core/Icon";
import { Field, idMensaje } from "./Field";
import c from "./Control.module.css";

export interface InputProps extends Omit<React.ComponentPropsWithoutRef<"input">, "size"> {
  label?: React.ReactNode;
  /** Línea de ayuda bajo el campo. */
  hint?: React.ReactNode;
  /** Qué pasó y qué hacer. Pinta el borde de rojo y se anuncia como alerta. */
  error?: React.ReactNode;
  /** Icono a la izquierda. */
  icon?: IconName;
  /** Texto fijo a la derecha: "m²", "USD", "min". */
  suffix?: React.ReactNode;
  /** md mide como un `Button` md (40); lg como uno lg (48): el campo único de una pantalla pública. */
  size?: "md" | "lg";
  /** Borde rojo sin mensaje debajo: el error se cuenta en otro lado (un `Alert` del formulario). */
  invalid?: boolean;
}

/**
 * Campo de una línea. Mide lo mismo que un `Button` md. Sin hooks de estado:
 * el foco y el error los dibuja el CSS. `className` y `style` van al envoltorio
 * (para ubicarlo en una grilla); el resto de las props, al `<input>`.
 */
export function Input({
  label,
  hint,
  error,
  icon,
  suffix,
  size = "md",
  invalid,
  id,
  type = "text",
  inputMode,
  required,
  className,
  style,
  ...rest
}: InputProps) {
  const generado = React.useId();
  const uid = id ?? generado;
  return (
    <Field htmlFor={uid} label={label} hint={hint} error={error} required={required} className={className} style={style}>
      <span className={c.caja} data-error={error || invalid ? "" : undefined} data-size={size === "lg" ? "lg" : undefined}>
        {icon ? (
          <span className={c.icono}>
            <Icon name={icon} size={16} />
          </span>
        ) : null}
        <input
          id={uid}
          type={type}
          inputMode={inputMode ?? (type === "number" ? "decimal" : undefined)}
          required={required}
          aria-invalid={error || invalid ? true : undefined}
          aria-describedby={error || hint ? idMensaje(uid) : undefined}
          className={c.control}
          {...rest}
        />
        {suffix ? <span className={c.sufijo}>{suffix}</span> : null}
      </span>
    </Field>
  );
}
